import isEqual from 'lodash/isEqual';
import {
  Dispatch,
  createContext,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useImmerReducer } from 'use-immer';
import uploadFiles from '../helper/uploadFile';
import captureManagerReducer from './reducer';
import {
  CaptureManagerAction,
  CaptureManagerContextProps,
  CaptureManagerProps,
  CaptureManagerState,
  IMPORT_FILES,
} from './type';

const initialState: Partial<CaptureManagerState> = {
  storeFiles: [],
  processingList: new Map(),
  isConverting: { enable: false, status: false },
};

export const CaptureManagerStateContext = createContext<CaptureManagerContextProps>(initialState);
const CaptureManagerUpdaterContext = createContext<Dispatch<CaptureManagerAction>>(null);

const CaptureManagerProvider = ({
  actionUpload,
  params,
  appearance,
  children,
  onUploading,
}: // study,
CaptureManagerProps) => {
  const [state, dispatch] = useImmerReducer(captureManagerReducer, initialState);
  const [showCamera, setShowCamera] = useState(appearance === 'default');
  const importRef = useRef<HTMLInputElement>(null);

  const importFiles = useCallback(
    (payload: IMPORT_FILES['payload']) => dispatch({ type: 'IMPORT_FILES', payload }),
    [dispatch]
  );

  const handleUploadFiles = useCallback(async () => {
    if (state.isConverting.status !== false && state.isConverting.enable) return;
    if (!state.selected?.length) return;
    const processingList = Array.from(state.processingList.values());
    if (state.selected?.length && processingList.every((x) => x.status === 'uploading')) return; // Prevent multiple simultaneous uploads

    const validIds = processingList.reduce((acc, x) => {
      if (['ready', 'exception'].includes(x.status)) acc.add(x.id);
      return acc;
    }, new Set<string>());

    const selectedList = state.selected.filter((x) => validIds.has(x.id));

    try {
      onUploading?.(true);
      const response = await uploadFiles({
        actionUpload,
        dispatch,
        params,
        selected: selectedList,
        storeFiles: state.storeFiles,
        processingList: state.processingList,
      });

      if (response) {
        dispatch({
          type: 'SET_CONVERTING',
          payload: { enable: false, status: null },
        });
      }

      return response;
    } catch (e) {
      console.error(`📕 e - 81:CaptureManager/context/index.tsx \n`, e);

      dispatch({ 'type': 'SET_ERROR', payload: { id: selectedList.map((x) => x.id) } });
      return null;
    } finally {
      onUploading?.(false);
    }
  }, [state, actionUpload, dispatch, params, onUploading]);

  useEffect(() => {
    const enableCheckConvert = state.isConverting.enable;
    if (enableCheckConvert) handleUploadFiles();
  }, [state.isConverting]);

  const memorizedValue = useMemo(
    () => ({
      ...state,
      showCamera,
      params,
      appearance,
      importRef,
      importFiles,
      //? Add for pass to useImperativeHandle
      setShowCamera,
      handleUploadFiles,
      actionUpload,
    }),
    [actionUpload, appearance, handleUploadFiles, importFiles, params, showCamera, state]
  ) as unknown as CaptureManagerContextProps;

  return (
    <CaptureManagerStateContext.Provider value={memorizedValue}>
      <CaptureManagerUpdaterContext.Provider value={dispatch}>
        {children}
      </CaptureManagerUpdaterContext.Provider>
    </CaptureManagerStateContext.Provider>
  );
};

export default memo(
  CaptureManagerProvider,
  (prev, next) =>
    isEqual(prev.params, next.params) &&
    prev.appearance === next.appearance &&
    isEqual(prev.actionUpload, next.actionUpload)
);

export const useCaptureManagerState = () => {
  const context = useContext(CaptureManagerStateContext);

  if (!context) {
    throw new Error('useCaptureManagerState must be used within a CaptureManagerProvider');
  }

  return context;
};

export const useCaptureManagerUpdater = () => {
  const context = useContext(CaptureManagerUpdaterContext);

  if (!context) {
    throw new Error('useCaptureManagerUpdater must be used within a CaptureManagerUpdaterContext');
  }

  return context;
};
